import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { distanceBetween } from '../utils/geo';

export const useRestaurantsListStore = defineStore('restaurantsList', () => {
    const route = useRoute();

    const perPage = 10;

    const concepts = ref([]);
    const restaurants = ref([]);
    const activeFilter = ref(null);
    const currentPage = ref(1);
    const searchedCoords = ref(null);

    watch(
        () => route.params.concept,
        (value) => (activeFilter.value = value),
    );
    watch(
        () => route.params.page,
        (value) => (currentPage.value = parseInt(value || 1)),
    );

    const filteredRestaurants = computed(() => {
        return !activeFilter.value ? restaurants.value : restaurants.value.filter((r) => r.concept.slug == activeFilter.value);
    });

    const searchedRestaurants = computed(() => {
        let results = filteredRestaurants.value;

        if (isSearching.value) {

            results = results.map((res) => {
                res.distance = distanceBetween(searchedCoords.value, { lat: res.latitude, lng: res.longitude });

                return res;
            });

            results = results.filter((res) => res.distance <= 25);

            results.sort((a, b) => a.distance - b.distance);
        }

        return results.map((item, index) => {
            item.number = (index + 1).toString().padStart(2, '0');
            return item;
        });
    });

    const currentRestaurants = computed(() => {
        const start = (currentPage.value - 1) * 10;

        return searchedRestaurants.value.slice(start, start + perPage);
    });

    const numPages = computed(() => {
        return Math.ceil(searchedRestaurants.value.length / perPage);
    });

    const activeFilterName = computed(() => {
        if (!activeFilter.value) {
            return '';
        }

        return concepts.value.find((c) => c.slug == activeFilter.value).name;
    });

    const isSearching = computed(() => !!searchedCoords.value);

    watch(currentPage, scrollToTop);

    /**
     * Scroll to the top of the restaurants list.
     */
    function scrollToTop() {
        nextTick(() => {
            const el = document.getElementById('restaurants-list-top');

            if (el) {
                el.scrollIntoView(true, { behavior: 'smooth' });
            }
        });
    }

    /**
     * Set the searched coordinates value.
     *
     * @param {object} coords The lat/lng coordinates.
     */
    function setSearchedCoords(coords) {
        searchedCoords.value = coords;
    }

    /**
     * Set the concepts value.
     *
     * @param {Array} value The concepts data.
     */
    function setConcepts(value) {
        concepts.value = value;
    }

    /**
     * Set the restaurants value.
     *
     * @param {Array} value The restaurants data.
     */
    function setRestaurants(value) {
        restaurants.value = value;
    }

    return {
        concepts,
        restaurants,
        activeFilter,

        searchedRestaurants,
        currentRestaurants,
        currentPage,
        numPages,
        activeFilterName,
        isSearching,

        setConcepts,
        setRestaurants,
        setSearchedCoords,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useRestaurantsListStore, import.meta.hot));
}
